import { c as _c } from "react/compiler-runtime";
import type { L10n } from '@onefootprint/footprint-js-legacy';
import { Idv2, Idv2Provider, LoadNeuroId } from '@onefootprint/idv2';
import { getErrorMessage } from '@onefootprint/request';
import type { IdDocOutcome, OverallOutcome } from '@onefootprint/types';
import { SessionStatus } from '@onefootprint/types';
import { type ComponentProps, useEffect, useMemo } from 'react';
import { type DoneArgs, Identify } from '@/idv/components/identify';
import { IdentifyVariant } from '@/idv/components/identify/identify.types';
import GenericErrorPage from '../../../src/components/gerenic-error-page/generic-error-page';
import { IDV_SESSION_RETRY_LIMIT } from '../../../src/config/constants';
import { AppErrorBoundary, SessionExpired, useLayoutOptions } from '../../components';
import { L10nContextProvider } from '../../components/l10n-provider';
import { useIdvMachine, useLogStateMachine } from '../../hooks';
import { useValidateSession } from '../../queries';
import { convertBootstrapToVaultData, FPCustomEvents, getLogger, trackAction } from '../../utils';
import { createAuthTokenChangedPayload, createReceivedDeviceResponseJsonPayload } from '../../utils/state-machine/utils/custom-listener';
import ConfigInvalid from '../config-invalid';
import Init from '../init';
import Onboarding from '../onboarding';
type RouterProps = {
  l10n?: L10n;
  onIdentifyDone?: ({
    authToken
  }: {
    authToken: string;
  }) => void;
};
const {
  receivedDeviceResponseJson,
  stepUpCompleted
} = FPCustomEvents;
const {
  logWarn
} = getLogger({
  location: 'idv-router'
});

/**
 * Renders inside Idv2Provider.
 * During sandbox-outcome: renders <Idv2 initialPath="/sandbox-outcome">.
 * During onboarding: renders <Onboarding>. The inner router
 * (onboarding/pages/router/router.tsx) handles relayToIDV2 and renders
 * <Idv2> as a child when true — keeping xstate alive across relay toggles.
 */
const Idv2OrLegacy = t0 => {
  const $ = _c(3);
  const {
    isSandboxOutcome,
    onboardingProps
  } = t0;
  if (isSandboxOutcome) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Idv2 initialPath="/sandbox-outcome" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== onboardingProps) {
    t1 = <Onboarding {...onboardingProps} />;
    $[1] = onboardingProps;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const Router = t0 => {
  const $ = _c(77);
  const {
    l10n,
    onIdentifyDone
  } = t0;
  const [state, send, service] = useIdvMachine();
  useLogStateMachine("idv", state);
  const {
    options: layoutOptions
  } = useLayoutOptions();
  const {
    config,
    device,
    authToken,
    bootstrapData,
    isTransfer,
    componentsSdkContext,
    isInIframe,
    obConfigAuth,
    idDocOutcome,
    overallOutcome,
    onClose,
    onComplete,
    autoAdvance,
    onBackButtonVisibilityChange,
    onRequirementChange,
    onExternalBack,
    retries,
    showLogo,
    sandboxId,
    fixtureRiskSignals
  } = state.context;
  const isIdv2Ready = !!config && !!device;
  const retryLimitExceeded = retries > IDV_SESSION_RETRY_LIMIT;
  let t1;
  if ($[0] !== bootstrapData) {
    t1 = convertBootstrapToVaultData(bootstrapData);
    $[0] = bootstrapData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    kycData: kycBootstrapData,
    kybData: kybBootstrapData
  } = t1;
  let t2;
  if ($[2] !== authToken) {
    t2 = {
      authToken
    };
    $[2] = authToken;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== send) {
    t3 = {
      onSuccess: sessionStatus => {
        if (sessionStatus !== SessionStatus.active) {
          send({
            type: "expireSession"
          });
          trackAction("expired:check_session", {
            sessionStatus
          });
          logWarn(`Session status: ${sessionStatus}`);
        }
      },
      onError: _temp
    };
    $[4] = send;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useValidateSession(t2, t3);
  let t4;
  if ($[6] !== send) {
    t4 = () => {
      const authTokenListener = e => {
        const payload = createAuthTokenChangedPayload(e);
        if (payload) {
          send(payload);
        }
      };
      const deviceResponseJsonListener = e_0 => {
        const payload_0 = createReceivedDeviceResponseJsonPayload(e_0);
        if (payload_0) {
          send(payload_0);
        }
      };
      document.addEventListener(stepUpCompleted, authTokenListener);
      document.addEventListener(receivedDeviceResponseJson, deviceResponseJsonListener);
      return function cleanup() {
        document.removeEventListener(stepUpCompleted, authTokenListener);
        document.removeEventListener(receivedDeviceResponseJson, deviceResponseJsonListener);
      };
    };
    $[6] = send;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== onIdentifyDone || $[10] !== send) {
    t6 = payload_1 => {
      send({
        type: "identifyCompleted",
        payload: payload_1
      });
      if (onIdentifyDone && payload_1) {
        onIdentifyDone(payload_1);
      }
      trackAction("identify:completed");
    };
    $[9] = onIdentifyDone;
    $[10] = send;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleIdentifyDone = t6;
  let t7;
  if ($[12] !== send) {
    t7 = () => send({
      type: "reset"
    });
    $[12] = send;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== config || $[15] !== isTransfer) {
    t8 = !isTransfer && <LoadNeuroId config={config} />;
    $[14] = config;
    $[15] = isTransfer;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== state) {
    t9 = state.matches("init") ? <Init /> : null;
    $[17] = state;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== authToken || $[20] !== bootstrapData || $[21] !== componentsSdkContext || $[22] !== config || $[23] !== device || $[24] !== handleIdentifyDone || $[25] !== l10n || $[26] !== obConfigAuth || $[27] !== onBackButtonVisibilityChange || $[28] !== onExternalBack || $[29] !== overallOutcome || $[30] !== sandboxId || $[31] !== showLogo || $[32] !== state) {
    t10 = state.matches("identify") && config && device ? <L10nContextProvider l10n={l10n}><Identify initArgs={{
        variant: IdentifyVariant.verify,
        device,
        config,
        isLive: config.isLive,
        overallOutcome,
        sandboxId,
        initialAuthToken: authToken,
        obConfigAuth,
        isComponentsSdk: !!componentsSdkContext,
        bootstrapData,
        logoConfig: showLogo ? {
          orgName: config.orgName,
          logoUrl: config.logoUrl || undefined
        } : undefined
      }} onDone={handleIdentifyDone} onBackButtonVisibilityChange={onBackButtonVisibilityChange} onExternalBack={onExternalBack} /></L10nContextProvider> : null;
    $[19] = authToken;
    $[20] = bootstrapData;
    $[21] = componentsSdkContext;
    $[22] = config;
    $[23] = device;
    $[24] = handleIdentifyDone;
    $[25] = l10n;
    $[26] = obConfigAuth;
    $[27] = onBackButtonVisibilityChange;
    $[28] = onExternalBack;
    $[29] = overallOutcome;
    $[30] = sandboxId;
    $[31] = showLogo;
    $[32] = state;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== authToken || $[35] !== autoAdvance || $[36] !== componentsSdkContext || $[37] !== config || $[38] !== device || $[39] !== fixtureRiskSignals || $[40] !== idDocOutcome || $[41] !== isIdv2Ready || $[42] !== isInIframe || $[43] !== isTransfer || $[44] !== kybBootstrapData || $[45] !== kycBootstrapData || $[46] !== l10n?.language || $[47] !== l10n?.locale || $[48] !== layoutOptions?.hideHeaderButton || $[49] !== obConfigAuth || $[50] !== onBackButtonVisibilityChange || $[51] !== onClose || $[52] !== onComplete || $[53] !== onRequirementChange || $[54] !== overallOutcome || $[55] !== sandboxId || $[56] !== send || $[57] !== service || $[58] !== state) {
    t11 = (state.matches("sandboxOutcome") || state.matches("onboarding")) && isIdv2Ready ? <Idv2Provider initialValues={{
      onboardingSessionToken: authToken ?? "",
      config,
      overallOutcome,
      fixtureRiskSignals,
      idDocOutcome,
      l10n: {
        locale: l10n?.locale,
        language: l10n?.language ?? "en"
      },
      obConfigAuth: state.matches("sandboxOutcome") ? obConfigAuth : undefined,
      kycBootstrapData,
      kybBootstrapData,
      isTransfer,
      sandboxId,
      isComponentsSdk: !!componentsSdkContext
    }} onComplete={(validationToken, options) => {
      if (!options?.hasCompletionScreen) {
        send({
          type: "onboardingCompleted",
          payload: {
            validationToken
          }
        });
      }
      trackAction("onboarding:completed");
      onComplete?.({
        validationToken,
        authToken,
        deviceResponseJson: service.getSnapshot().context.deviceResponseJson,
        hasCompletionScreen: options?.hasCompletionScreen
      });
    }} onHandoffComplete={() => {
      onComplete?.({});
    }} onSandboxOutcomeSubmit={payload_2 => send({
      type: "sandboxOutcomeSubmitted",
      payload: {
        overallOutcome: payload_2.overallOutcome as OverallOutcome | undefined,
        fixtureRiskSignals: payload_2.fixtureRiskSignals,
        idDocOutcome: payload_2.idDocOutcome as IdDocOutcome | undefined,
        sandboxId: payload_2.sandboxId
      }
    })} onClose={onClose} autoAdvance={autoAdvance} onBackButtonVisibilityChange={onBackButtonVisibilityChange} onRequirementChange={onRequirementChange} hideNavigationButtons={layoutOptions?.hideHeaderButton} componentsSdkRelay={componentsSdkContext && !componentsSdkContext.skipRelayToComponents ? {
      relayToComponents: componentsSdkContext.relayToComponents,
      onRelayFromComponents: componentsSdkContext.onRelayFromComponents
    } : undefined}><Idv2OrLegacy isSandboxOutcome={state.matches("sandboxOutcome")} onboardingProps={{
        config,
        idvContext: {
          device,
          authToken: authToken ?? "",
          isTransfer,
          componentsSdkContext,
          isInIframe
        },
        overallOutcome,
        fixtureRiskSignals,
        idDocOutcome: idDocOutcome as IdDocOutcome | undefined,
        onClose
      }} /></Idv2Provider> : null;
    $[34] = authToken;
    $[35] = autoAdvance;
    $[36] = componentsSdkContext;
    $[37] = config;
    $[38] = device;
    $[39] = fixtureRiskSignals;
    $[40] = idDocOutcome;
    $[41] = isIdv2Ready;
    $[42] = isInIframe;
    $[43] = isTransfer;
    $[44] = kybBootstrapData;
    $[45] = kycBootstrapData;
    $[46] = l10n?.language;
    $[47] = l10n?.locale;
    $[48] = layoutOptions?.hideHeaderButton;
    $[49] = obConfigAuth;
    $[50] = onBackButtonVisibilityChange;
    $[51] = onClose;
    $[52] = onComplete;
    $[53] = onRequirementChange;
    $[54] = overallOutcome;
    $[55] = sandboxId;
    $[56] = send;
    $[57] = service;
    $[58] = state;
    $[59] = t11;
  } else {
    t11 = $[59];
  }
  let t12;
  if ($[60] !== state) {
    t12 = state.matches("sessionExpired") ? <SessionExpired /> : null;
    $[60] = state;
    $[61] = t12;
  } else {
    t12 = $[61];
  }
  let t13;
  if ($[62] !== retryLimitExceeded || $[63] !== send || $[64] !== state) {
    t13 = state.matches("initConfigFailed") ? <GenericErrorPage onRetry={() => send({
      type: "reset"
    })} retryLimitExceeded={retryLimitExceeded} /> : null;
    $[62] = retryLimitExceeded;
    $[63] = send;
    $[64] = state;
    $[65] = t13;
  } else {
    t13 = $[65];
  }
  let t14;
  if ($[66] !== state) {
    t14 = state.matches("configInvalid") ? <ConfigInvalid /> : null;
    $[66] = state;
    $[67] = t14;
  } else {
    t14 = $[67];
  }
  let t15;
  if ($[68] !== t10 || $[69] !== t11 || $[70] !== t12 || $[71] !== t13 || $[72] !== t14 || $[73] !== t7 || $[74] !== t8 || $[75] !== t9) {
    t15 = <AppErrorBoundary onReset={t7}>{t8}{t9}{t10}{t11}{t12}{t13}{t14}</AppErrorBoundary>;
    $[68] = t10;
    $[69] = t11;
    $[70] = t12;
    $[71] = t13;
    $[72] = t14;
    $[73] = t7;
    $[74] = t8;
    $[75] = t9;
    $[76] = t15;
  } else {
    t15 = $[76];
  }
  return t15;
};
export default Router;
function _temp(error) {
  logWarn(`Validating user session failed with error: ${getErrorMessage(error)}`, error);
}